/**
 * ScopeSelector — seletor de escopo de visualizacao de chamados.
 *
 * Spec: docs/CHAMADOS_VISIBILIDADE_SPEC.md §9.3.1
 *
 * Comportamento:
 *  - Desktop: SegmentedControl Mantine com role="radiogroup"
 *  - Mobile (<= 768px): Select Mantine
 *  - G-N10: se loading OU allowedScopes.length <= 1, renderiza apenas
 *    o label atual em modo nao-interativo (evita flicker durante resolucao
 *    de permissions/orgs).
 *  - Color: 'inovareTeal' (paleta institucional registrada em theme/index.ts;
 *    o token anterior "brandVerde" so existe em aiVars e nao eh uma
 *    Mantine color tuple — caia no fallback default, deixando contraste
 *    ilegivel no light mode).
 */
import { SegmentedControl, Select, Group, Text, Tooltip } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import type { ChamadoScope } from '@/hooks/useScope'

export interface ScopeSelectorProps {
  scope: ChamadoScope
  setScope: (s: ChamadoScope) => void
  allowedScopes: ChamadoScope[]
  loading?: boolean
}

const LABELS: Record<ChamadoScope, string> = {
  global: 'Todos (DevOps)',
  org: 'Minha organização',
  own: 'Meus chamados',
}

const TOOLTIPS: Record<ChamadoScope, string> = {
  global: 'Visualiza chamados de todos os tenants (requer permissão DevOps).',
  org: 'Visualiza chamados dos tenants da sua organização.',
  own: 'Visualiza somente os seus chamados.',
}

export function ScopeSelector({
  scope,
  setScope,
  allowedScopes,
  loading = false,
}: ScopeSelectorProps) {
  const isMobile = useMediaQuery('(max-width: 768px)')

  // G-N10: evita render interativo durante resolucao OU quando ha apenas 1 escopo.
  if (loading || allowedScopes.length <= 1) {
    return (
      <Group gap="xs" aria-live="polite">
        <Text size="sm" c="dimmed">
          Escopo:
        </Text>
        <Text size="sm" fw={500}>
          {LABELS[scope]}
        </Text>
      </Group>
    )
  }

  const data = allowedScopes.map((s) => ({ value: s, label: LABELS[s] }))

  if (isMobile) {
    return (
      <Tooltip label={TOOLTIPS[scope]} position="bottom" withArrow>
        <Select
          data={data}
          value={scope}
          onChange={(v) => v && setScope(v as ChamadoScope)}
          label="Escopo"
          size="sm"
          aria-label="Selecionar escopo de visualização"
        />
      </Tooltip>
    )
  }

  return (
    <Tooltip label={TOOLTIPS[scope]} position="bottom" withArrow>
      <SegmentedControl
        data={data}
        value={scope}
        onChange={(v) => setScope(v as ChamadoScope)}
        color="inovareTeal"
        size="sm"
        role="radiogroup"
        aria-label="Selecionar escopo de visualização"
      />
    </Tooltip>
  )
}
